import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, XCircle, Loader, RefreshCw, 
  Mic, Volume2, Brain, Database, Globe, 
  Activity, AlertCircle, Info, Play, Clock
} from 'lucide-react';

interface TestResult {
  name: string;
  endpoint: string;
  status: 'pending' | 'testing' | 'success' | 'error' | 'warning';
  message: string;
  duration?: number;
  details?: any;
  timestamp?: string;
}

interface TestCategory {
  name: string;
  icon: React.ReactNode;
  tests: TestResult[];
}

export const TestDashboard: React.FC = () => {
  const [categories, setCategories] = useState<TestCategory[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState('');
  const [testLog, setTestLog] = useState<string[]>([]);

  useEffect(() => {
    const url = import.meta.env.VITE_API_URL || 'http://localhost:8787';
    setApiUrl(url);
    initializeTests();
    loadTestHistory();
  }, []);

  const initializeTests = () => {
    setCategories([
      {
        name: 'API Health',
        icon: <Activity className="w-5 h-5" />,
        tests: [
          { name: 'API Server', endpoint: '/', status: 'pending', message: 'Not tested' },
          { name: 'Health Check', endpoint: '/api/health', status: 'pending', message: 'Not tested' },
          { name: 'CORS Headers', endpoint: '/api/hello', status: 'pending', message: 'Not tested' },
        ]
      },
      {
        name: 'Audio Services',
        icon: <Volume2 className="w-5 h-5" />,
        tests: [
          { name: 'ElevenLabs TTS', endpoint: '/api/audio/generate', status: 'pending', message: 'Not tested' },
          { name: 'Audio Transcription', endpoint: '/api/audio-input/transcribe', status: 'pending', message: 'Not tested' },
          { name: 'Audio Storage', endpoint: '/api/recordings/list', status: 'pending', message: 'Not tested' },
        ]
      },
      {
        name: 'AI Services',
        icon: <Brain className="w-5 h-5" />,
        tests: [
          { name: 'Coach AI', endpoint: '/api/coach', status: 'pending', message: 'Not tested' },
          { name: 'Self-Talk Generator', endpoint: '/api/selftalk/generate', status: 'pending', message: 'Not tested' },
          { name: 'Cloudflare AI', endpoint: '/api/coach', status: 'pending', message: 'Not tested' },
        ]
      },
      {
        name: 'Integrations',
        icon: <Globe className="w-5 h-5" />,
        tests: [
          { name: 'Instagram API', endpoint: '/api/instagram/feed', status: 'pending', message: 'Not tested' },
          { name: 'Spotify Playlists', endpoint: '/api/spotify/motivational', status: 'pending', message: 'Not tested' },
        ]
      },
      {
        name: 'Browser Features',
        icon: <Mic className="w-5 h-5" />,
        tests: [
          { name: 'Microphone Access', endpoint: 'browser:mic', status: 'pending', message: 'Not tested' },
          { name: 'Speech Synthesis', endpoint: 'browser:tts', status: 'pending', message: 'Not tested' },
          { name: 'LocalStorage', endpoint: 'browser:storage', status: 'pending', message: 'Not tested' },
          { name: 'Web Audio API', endpoint: 'browser:audio', status: 'pending', message: 'Not tested' },
        ]
      }
    ]);
  };

  const loadTestHistory = () => {
    const history = localStorage.getItem('test_history');
    if (history) {
      const parsed = JSON.parse(history);
      setLastRun(parsed.lastRun);
      setTestLog(parsed.log || []);
    }
  };

  const saveTestHistory = (results: TestCategory[]) => {
    const history = {
      lastRun: new Date().toISOString(),
      results: results,
      log: testLog
    };
    localStorage.setItem('test_history', JSON.stringify(history));
  };

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    setTestLog(prev => [logEntry, ...prev].slice(0, 100)); // Keep last 100 entries
  };

  const runAllTests = async () => {
    setIsRunning(true);
    addLog('Starting comprehensive test suite...');
    
    const newCategories = [...categories];
    
    for (const category of newCategories) {
      addLog(`Testing category: ${category.name}`);
      
      for (const test of category.tests) {
        test.status = 'testing';
        test.timestamp = new Date().toISOString();
        setCategories([...newCategories]);
        
        const startTime = Date.now();
        
        try {
          if (test.endpoint.startsWith('browser:')) {
            await runBrowserTest(test);
          } else {
            await runApiTest(test);
          }
          
          test.duration = Date.now() - startTime;
          test.status = 'success';
          addLog(`✓ ${test.name}: ${test.message}`);
        } catch (error: any) {
          test.duration = Date.now() - startTime;
          test.status = 'error';
          test.message = error.message || 'Test failed';
          addLog(`✗ ${test.name}: ${test.message}`);
        }
        
        setCategories([...newCategories]);
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay between tests
      }
    }
    
    setLastRun(new Date().toISOString());
    saveTestHistory(newCategories);
    setIsRunning(false);
    addLog('Test suite completed!');
  };

  const runApiTest = async (test: TestResult) => {
    const url = `${apiUrl}${test.endpoint}`;
    
    try {
      let response;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      
      switch (test.endpoint) {
        case '/api/audio/generate':
          response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: 'Test audio generation', voice: 'rachel' }),
            signal: controller.signal
          });
          break;
          
        case '/api/audio-input/transcribe':
          response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ audio: 'dGVzdA==', format: 'webm' }),
            signal: controller.signal
          });
          break;
          
        case '/api/coach':
        case '/api/selftalk/generate':
          response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              input: 'Test motivation request',
              prompt: 'Test prompt',
              category: 'motivation'
            }),
            signal: controller.signal
          });
          break;
          
        default:
          response = await fetch(url, {
            method: 'GET',
            signal: controller.signal
          });
      }
      
      clearTimeout(timeout);
      
      if (response.ok) {
        const data = await response.json();
        test.message = `Connected (${response.status})`;
        test.details = data;
        
        // Check for specific features
        if (test.name === 'ElevenLabs TTS' && !data.audio_url) {
          test.status = 'warning';
          test.message = 'API works but no audio generated (check API key)';
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout (5s)');
      }
      throw error;
    }
  };

  const runBrowserTest = async (test: TestResult) => {
    switch (test.endpoint) {
      case 'browser:mic':
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(track => track.stop());
          test.message = 'Microphone access granted';
        } catch (error) {
          throw new Error('Microphone access denied');
        }
        break;
        
      case 'browser:tts':
        if ('speechSynthesis' in window) {
          test.message = `TTS available (${speechSynthesis.getVoices().length} voices)`;
        } else {
          throw new Error('Speech synthesis not supported');
        }
        break;
        
      case 'browser:storage':
        try {
          localStorage.setItem('test', 'value');
          localStorage.removeItem('test');
          const usage = JSON.stringify(localStorage).length;
          test.message = `Storage working (${(usage / 1024).toFixed(1)}KB used)`;
        } catch (error) {
          throw new Error('LocalStorage not available');
        }
        break;
        
      case 'browser:audio':
        if ('AudioContext' in window || 'webkitAudioContext' in window) {
          test.message = 'Web Audio API supported';
        } else {
          throw new Error('Web Audio API not supported');
        }
        break;
    }
  };

  const runSingleTest = async (categoryIndex: number, testIndex: number) => {
    const newCategories = [...categories];
    const test = newCategories[categoryIndex].tests[testIndex];
    
    test.status = 'testing';
    setCategories(newCategories);
    
    const startTime = Date.now();
    
    try {
      if (test.endpoint.startsWith('browser:')) {
        await runBrowserTest(test);
      } else {
        await runApiTest(test);
      }
      
      test.duration = Date.now() - startTime;
      test.status = 'success';
      addLog(`✓ ${test.name}: ${test.message}`);
    } catch (error: any) {
      test.duration = Date.now() - startTime;
      test.status = 'error';
      test.message = error.message || 'Test failed';
      addLog(`✗ ${test.name}: ${test.message}`);
    }
    
    setCategories(newCategories);
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'testing':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/10 border-green-500/30';
      case 'error':
        return 'bg-red-500/10 border-red-500/30';
      case 'warning':
        return 'bg-yellow-500/10 border-yellow-500/30';
      case 'testing':
        return 'bg-blue-500/10 border-blue-500/30';
      default:
        return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  const exportResults = () => {
    const results = {
      timestamp: new Date().toISOString(),
      apiUrl,
      categories,
      testLog
    };
    
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-results-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-slate-900 to-purple-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">System Test Dashboard</h1>
          <p className="text-gray-400">Comprehensive testing for all API endpoints and browser features</p>
          
          <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">API Endpoint</p>
                <p className="font-mono text-purple-400">{apiUrl}</p>
              </div>
              {lastRun && (
                <div className="text-right">
                  <p className="text-sm text-gray-400">Last Run</p>
                  <p className="text-sm">{new Date(lastRun).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={runAllTests}
            disabled={isRunning}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRunning ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run All Tests
              </>
            )}
          </button>
          
          <button
            onClick={initializeTests}
            className="flex items-center gap-2 px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Reset
          </button>
          
          <button
            onClick={exportResults}
            className="flex items-center gap-2 px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
          >
            Export Results
          </button>
        </div>

        {/* Test Categories */}
        <div className="grid gap-6 lg:grid-cols-2">
          {categories.map((category, catIndex) => (
            <div key={catIndex} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                  {category.icon}
                </div>
                <h2 className="text-xl font-semibold">{category.name}</h2>
              </div>
              
              <div className="space-y-3">
                {category.tests.map((test, testIndex) => (
                  <div
                    key={testIndex}
                    className={`p-4 rounded-lg border ${getStatusColor(test.status)} transition-all`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getStatusIcon(test.status)}
                        <div>
                          <h3 className="font-medium">{test.name}</h3>
                          <p className="text-sm text-gray-400 mt-1">{test.message}</p>
                          {test.duration !== undefined && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {test.duration}ms
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => runSingleTest(catIndex, testIndex)}
                        disabled={isRunning || test.status === 'testing'}
                        className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {test.details && test.status === 'success' && (
                      <details className="mt-3">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                          View Response
                        </summary>
                        <pre className="mt-2 p-2 bg-black/30 rounded text-xs overflow-x-auto">
                          {JSON.stringify(test.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Test Log */}
        <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold mb-4">Test Log</h2>
          <div className="bg-black/30 rounded-lg p-4 h-64 overflow-y-auto font-mono text-xs">
            {testLog.length > 0 ? (
              testLog.map((entry, index) => (
                <div key={index} className="text-gray-300 mb-1">
                  {entry}
                </div>
              ))
            ) : (
              <p className="text-gray-500">No test logs yet. Run tests to see activity.</p>
            )}
          </div>
        </div>

        {/* Quick Status Summary */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          {['success', 'error', 'warning', 'pending'].map(status => {
            const count = categories.flatMap(c => c.tests).filter(t => t.status === status).length;
            return (
              <div key={status} className="bg-white/5 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm text-gray-400 capitalize">{status}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};